<template>
  <aside class="side-menu">
    <div class="menu-header">
      <div class="logo-section" @click="goToDomains">
        <img alt="Segofy logo" src="../assets/logo.png" class="logo">
      </div>
    </div>
    
    <nav class="menu-nav">
      <div class="nav-item" :class="{ active: isActive('/domains') }" @click="goToDomains">
        <svg class="nav-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clip-rule="evenodd" />
        </svg>
        <span class="nav-text">Sites</span>
      </div>
      <div class="nav-item" :class="{ active: isActive('/profile') }" @click="goToAccount">
        <img src="../assets/menu-account.png" alt="Account" class="nav-icon">
        <span class="nav-text">Account</span>
      </div>
      <div v-if="subscriptionsEnabled" class="nav-item" :class="{ active: isActive('/subscriptions') }" @click="goToSubscriptions">
        <svg class="nav-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
        </svg>
        <span class="nav-text">Subscriptions</span>
      </div>
      <div v-if="subscriptionsEnabled" class="nav-item" :class="{ active: isActive('/payments') }" @click="goToPayments">
        <svg class="nav-icon" viewBox="0 0 20 20" fill="currentColor">
          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clip-rule="evenodd" />
        </svg>
        <span class="nav-text">Payments</span>
      </div>
    </nav>
    
    <div v-if="isAdmin" class="admin-link" @click="goToAdmin">
      <svg class="admin-icon" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" />
      </svg>
      <span class="admin-text">Admin</span>
    </div>

    <div class="menu-footer">
      <span class="version">API v{{ version }}</span>
    </div>
  </aside>
</template>

<script>
import { useRouter, useRoute } from 'vue-router'
import { ref, onMounted } from 'vue'
import { api, getCurrentUser } from '../utils/api'

export default {
  name: 'SideMenu',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const version = ref('')
    const isAdmin = ref(false)
    const envValue = process.env.VUE_APP_FEATURE_FLAG_SUBSCRIPTIONS_ENABLE
    // Check if enabled - handle string 'true', boolean true, or 'True'/'TRUE'
    const isEnabled = envValue === 'true' || envValue === true || String(envValue).toLowerCase() === 'true'
    const subscriptionsEnabled = ref(isEnabled)

    const fetchVersion = async () => {
      try {
        const data = await api('/version')
        version.value = data.version
      } catch (error) {
        console.error('Failed to fetch API version:', error)
        version.value = 'unknown'
      }
    }

    const fetchUser = async () => {
      // Don't fetch user data on login/register pages
      if (route.path === '/login' || route.path === '/register') {
        return
      }

      // Don't fetch user data if there's no token
      const token = (() => { try { return localStorage.getItem('fic') } catch (_) { return null } })()
      if (!token) {
        return
      }

      try {
        const userData = await getCurrentUser(router)
        isAdmin.value = userData?.isAdmin === true
      } catch (error) {
        console.error('Failed to fetch user data:', error)
        isAdmin.value = false
      }
    }

    const goToDomains = () => {
      router.push('/domains')
    }

    const goToAccount = () => {
      router.push('/profile')
    }

    const goToSubscriptions = () => {
      router.push('/subscriptions')
    }

    const goToPayments = () => {
      router.push('/payments')
    }

    const goToAdmin = () => {
      router.push('/admin')
    }

    const isActive = (path) => {
      return route.path === path || route.path.startsWith(path + '/')
    }

    onMounted(() => {
      fetchVersion()
      fetchUser()
    })

    return {
      version,
      isAdmin,
      subscriptionsEnabled,
      goToDomains,
      goToAccount,
      goToSubscriptions,
      goToPayments,
      goToAdmin,
      isActive
    }
  }
}
</script>

<style scoped>
.side-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 199px;
  height: 100vh;
  background-color: var(--bg-secondary);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
}

.menu-header {
  padding: 20px 20px 40px 20px;
}

.logo-section {
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 20px;
}

.logo {
  height: 40px;
  transition: transform 0.2s;
}

.logo:hover {
  transform: scale(1.05);
}

.menu-nav {
  flex: 1;
  padding: 20px 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: rgba(255, 255, 255, 0.8);
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-right: 3px solid var(--accent-color, #007bff);
}

.nav-icon {
  margin-right: 12px;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.nav-text {
  font-weight: 500;
  font-size: 16px;
}

.admin-link {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.2s;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.admin-link:hover {
  background-color: rgba(139, 92, 246, 0.15);
  color: rgba(255, 255, 255, 0.8);
}

.admin-icon {
  width: 14px;
  height: 14px;
  margin-right: 8px;
}

.admin-text {
  font-weight: 500;
}

.menu-footer {
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
}

.version {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  opacity: 0.7;
}
</style> 